import { calculateCourseHandicap, adjustHandicapsOffLow, getStrokeHoles, buildHandicapBreakdown, buildFullHandicapBreakdown, buildTeamHandicapBreakdown, buildScrambleHandicapBreakdown, resolvePlayerTeeRatings, resolvePlayerHandicapIndex } from './handicaps'
import { getHandicapPercent, get3v4HandicapPercents } from '../constants/gameTypes'

/**
 * Build game-type-aware handicap breakdown.
 * Pure function — safe to call in loops (e.g. MoneyBoard).
 * Handles 3v4 detection, stroke play percentages, match play differential method.
 * When mixedTees is provided, computes per-player course handicaps from their own tee.
 *
 * Routing:
 * - 3v4 path → buildTeamHandicapBreakdown (handles both match/stroke play internally)
 * - Stroke play (net_stroke_play, duels, six_pack, stableford) → buildFullHandicapBreakdown with percentage
 * - Match play (solo_match_play, top2, hero, four_ball, next_ball) → buildHandicapBreakdown (differential method)
 *
 * @param {Object} params
 * @param {string} params.gameType - game type id (e.g. 'top2', 'stableford')
 * @param {Array} params.teamStructure - [{ playerIds: [...] }]
 * @param {Array} params.matchPlayers - [{ player_id, handicap_index }]
 * @param {Array} [params.gamePlayers] - [{ player_id }] filter; if omitted, all matchPlayers are used
 * @param {Object} params.course - course object with par[], handicapIndex[]
 * @param {Object} params.tee - tee object with slope, rating (primary/default tee)
 * @param {Object} [params.mixedTees] - { [playerId]: { slope, rating, handicapIndex? } } per-player tee overrides
 * @returns {Array|null} handicap breakdown array or null
 */
export function buildGameHandicapBreakdown({ gameType, teamStructure, matchPlayers, gamePlayers, course, tee, mixedTees, gameConfig }) {
  if (!course || !tee || !matchPlayers?.length) return null

  const playerData = (gamePlayers
    ? matchPlayers.filter((mp) => gamePlayers.some((gp) => gp.player_id === mp.player_id))
    : matchPlayers
  ).map((mp) => ({
    playerId: mp.player_id,
    handicapIndex: Number(mp.handicap_index),
    gender: mp.gender || 'M',
  }))

  if (playerData.length === 0) return null

  // Universal straight-up: no handicap strokes for any game type
  if (gameConfig?.straightUp) {
    return playerData.map((p) => ({
      ...p, courseHandicap: 0, adjustedStrokes: 0, strokeHoles: new Map(),
    }))
  }

  const totalPar = course.par.reduce((s, p) => s + p, 0)

  // Session-wide off-low: when this game is a subset of a larger session,
  // compute the lowest course handicap across ALL match players so every game
  // in the session shares the same baseline.
  const sessionLowCH = computeSessionLowCH(gamePlayers, matchPlayers, tee, mixedTees, totalPar, gameConfig?.offLowScope)

  // Custom stroke mode: user-specified percentage and off-low preference
  if (gameConfig?.strokeMode === 'custom') {
    const pct = gameConfig.customHandicapPercent ?? 1.0
    const useOffLow = gameConfig.customOffLow ?? false

    // If mixed tees or mixed genders, route through mixed tee path (it will see custom config)
    const hasFemale = playerData.some((p) => p.gender === 'F')
    const effectiveMixedTees = mixedTees || (hasFemale ? {} : null)
    if (effectiveMixedTees) {
      return buildMixedTeeBreakdown({ gameType, teamStructure, playerData, course, tee, mixedTees: effectiveMixedTees, totalPar, sessionLowCH, gameConfig })
    }

    if (useOffLow) {
      return buildHandicapBreakdown(playerData, tee.slope, tee.rating, totalPar, course.handicapIndex, pct, sessionLowCH)
    } else {
      return buildFullHandicapBreakdown(playerData, tee.slope, tee.rating, totalPar, course.handicapIndex, pct)
    }
  }

  // If any player is female, they need different slope/rating even on the same tee.
  // Route through the mixed tee path which handles per-player resolution.
  const hasFemale = playerData.some((p) => p.gender === 'F')
  const effectiveMixedTees = mixedTees || (hasFemale ? {} : null)

  // Mixed tees or mixed genders: compute per-player course handicaps individually
  if (effectiveMixedTees) {
    return buildMixedTeeBreakdown({ gameType, teamStructure, playerData, course, tee, mixedTees: effectiveMixedTees, totalPar, sessionLowCH, gameConfig })
  }

  // Scramble: weighted team handicap formula (35% low + 15% high), off-low between teams
  if (gameType === 'scramble') {
    const straightUp = gameConfig?.straightUp || false
    return buildScrambleHandicapBreakdown(teamStructure, playerData, tee.slope, tee.rating, totalPar, course.handicapIndex, straightUp)
  }

  // 3v4 path: team-specific percentages with different off-low handling
  const percents3v4 = get3v4HandicapPercents(gameType, teamStructure)
  if (percents3v4) {
    const playerPercentMap = new Map()
    teamStructure.forEach((team) => {
      const pct = percents3v4[team.playerIds.length]
      team.playerIds.forEach((pid) => playerPercentMap.set(pid, pct))
    })
    const offLow = gameType !== 'stableford'
    return buildTeamHandicapBreakdown(playerData, tee.slope, tee.rating, totalPar, course.handicapIndex, playerPercentMap, offLow, sessionLowCH)
  }

  // Stroke play games: percentage of course handicap, no off-low
  const strokePlayGames = ['net_stroke_play', 'stableford', 'duels', 'six_pack']
  if (strokePlayGames.includes(gameType)) {
    return buildFullHandicapBreakdown(playerData, tee.slope, tee.rating, totalPar, course.handicapIndex, getHandicapPercent(gameType))
  }

  // Match play games: differential method (off-low first, then percentage)
  // solo_match_play, top2, hero, four_ball, next_ball
  return buildHandicapBreakdown(playerData, tee.slope, tee.rating, totalPar, course.handicapIndex, getHandicapPercent(gameType), sessionLowCH)
}

/**
 * Compute the session-wide lowest course handicap across all match players.
 * Returns undefined when there is no sub-game filter (gamePlayers absent),
 * meaning the game already contains all players and normal off-low applies.
 */
function computeSessionLowCH(gamePlayers, matchPlayers, tee, mixedTees, totalPar, offLowScope) {
  if (!gamePlayers) return undefined
  if (offLowScope === 'matchup') return undefined  // per-matchup off-low instead of session-wide
  const courseHandicaps = matchPlayers.map((mp) => {
    const playerTee = mixedTees?.[mp.player_id] || tee
    const { slope, rating } = resolvePlayerTeeRatings(playerTee, mp.gender || 'M')
    return calculateCourseHandicap(Number(mp.handicap_index), slope, rating, totalPar)
  })
  return Math.min(...courseHandicaps)
}

/**
 * Build handicap breakdown when players are on different tees.
 * Each player's course handicap is computed from their own tee's slope/rating.
 * Stroke holes use each player's tee handicap rankings when available.
 */
function buildMixedTeeBreakdown({ gameType, teamStructure, playerData, course, tee, mixedTees, totalPar, sessionLowCH, gameConfig }) {
  const isCustom = gameConfig?.strokeMode === 'custom'
  const handicapPercent = isCustom ? (gameConfig.customHandicapPercent ?? 1.0) : getHandicapPercent(gameType)
  const strokePlayGames = ['net_stroke_play', 'stableford', 'duels', 'six_pack']
  const isStrokePlay = isCustom
    ? !(gameConfig.customOffLow ?? false)  // custom: off-low OFF = stroke play path
    : strokePlayGames.includes(gameType)

  // Custom mode: skip scramble and 3v4 special paths, use simple percentage ± off-low
  if (isCustom) {
    const withCourseHandicaps = playerData.map((p) => {
      const playerTee = mixedTees[p.playerId] || tee
      const { slope, rating } = resolvePlayerTeeRatings(playerTee, p.gender || 'M')
      const courseHandicap = calculateCourseHandicap(p.handicapIndex, slope, rating, totalPar)
      const rankings = resolvePlayerHandicapIndex(playerTee, p.gender || 'M', course.handicapIndex)
      return { ...p, courseHandicap, rankings }
    })
    let withAdjusted
    if (isStrokePlay) {
      withAdjusted = withCourseHandicaps.map((p) => ({
        ...p, adjustedStrokes: Math.round(p.courseHandicap * handicapPercent),
      }))
    } else {
      const adjusted = adjustHandicapsOffLow(withCourseHandicaps, sessionLowCH)
      withAdjusted = adjusted.map((p) => ({
        ...p, adjustedStrokes: Math.round(p.adjustedStrokes * handicapPercent),
      }))
    }
    return withAdjusted.map((p) => ({
      ...p, strokeHoles: getStrokeHoles(p.adjustedStrokes, p.rankings), rankings: undefined,
    }))
  }

  // Scramble with mixed tees: compute per-player CH from their own tee, then apply team formula
  if (gameType === 'scramble') {
    const straightUp = gameConfig?.straightUp || false
    const withCourseHandicaps = playerData.map((p) => {
      const playerTee = mixedTees[p.playerId] || tee
      const { slope, rating } = resolvePlayerTeeRatings(playerTee, p.gender || 'M')
      const courseHandicap = calculateCourseHandicap(p.handicapIndex, slope, rating, totalPar)
      const rankings = resolvePlayerHandicapIndex(playerTee, p.gender || 'M', course.handicapIndex)
      return { ...p, courseHandicap, rankings }
    })

    if (straightUp) {
      return withCourseHandicaps.map((p) => ({
        ...p,
        adjustedStrokes: 0,
        strokeHoles: new Map(),
        rankings: undefined,
      }))
    }

    // Compute team handicaps using USGA scramble weights
    const teamHandicaps = teamStructure.map((team) => {
      const teamPlayers = team.playerIds
        .map((pid) => withCourseHandicaps.find((p) => p.playerId === pid))
        .filter(Boolean)
        .sort((a, b) => a.courseHandicap - b.courseHandicap)
      if (teamPlayers.length < 2) return 0
      return Math.round(teamPlayers[0].courseHandicap * 0.35 + teamPlayers[1].courseHandicap * 0.15)
    })

    const minTeamHandicap = Math.min(...teamHandicaps)
    const teamAdjusted = teamHandicaps.map((th) => th - minTeamHandicap)

    const teamPlayerMap = new Map()
    teamStructure.forEach((team, idx) => {
      team.playerIds.forEach((pid) => teamPlayerMap.set(pid, idx))
    })

    return withCourseHandicaps.map((p) => {
      const teamIdx = teamPlayerMap.get(p.playerId)
      const adjustedStrokes = teamIdx != null ? teamAdjusted[teamIdx] : 0
      return {
        ...p,
        adjustedStrokes,
        strokeHoles: getStrokeHoles(adjustedStrokes, p.rankings),
        rankings: undefined,
      }
    })
  }

  // 3v4 percentages (if applicable)
  const percents3v4 = get3v4HandicapPercents(gameType, teamStructure)
  let playerPercentMap = null
  if (percents3v4) {
    playerPercentMap = new Map()
    teamStructure.forEach((team) => {
      const pct = percents3v4[team.playerIds.length]
      team.playerIds.forEach((pid) => playerPercentMap.set(pid, pct))
    })
  }

  // Step 1: Compute each player's course handicap from their own tee (gender-aware)
  const withCourseHandicaps = playerData.map((p) => {
    const playerTee = mixedTees[p.playerId] || tee
    const { slope, rating } = resolvePlayerTeeRatings(playerTee, p.gender || 'M')
    const courseHandicap = calculateCourseHandicap(p.handicapIndex, slope, rating, totalPar)
    const rankings = resolvePlayerHandicapIndex(playerTee, p.gender || 'M', course.handicapIndex)
    return { ...p, courseHandicap, rankings }
  })

  // Step 2: Determine adjusted strokes based on game type
  let withAdjusted
  if (percents3v4) {
    // 3v4 path
    const offLow = gameType !== 'stableford'
    if (offLow) {
      const adjusted = adjustHandicapsOffLow(withCourseHandicaps, sessionLowCH)
      withAdjusted = adjusted.map((p) => {
        const percent = playerPercentMap.get(p.playerId) || 1.0
        return { ...p, adjustedStrokes: Math.round(p.adjustedStrokes * percent) }
      })
    } else {
      withAdjusted = withCourseHandicaps.map((p) => {
        const percent = playerPercentMap.get(p.playerId) || 1.0
        return { ...p, adjustedStrokes: Math.round(p.courseHandicap * percent) }
      })
    }
  } else if (isStrokePlay) {
    // Stroke play: percentage of course handicap, no off-low
    withAdjusted = withCourseHandicaps.map((p) => ({
      ...p,
      adjustedStrokes: Math.round(p.courseHandicap * handicapPercent),
    }))
  } else {
    // Match play: off-low first, then percentage
    const adjusted = adjustHandicapsOffLow(withCourseHandicaps, sessionLowCH)
    withAdjusted = adjusted.map((p) => ({
      ...p,
      adjustedStrokes: Math.round(p.adjustedStrokes * handicapPercent),
    }))
  }

  // Step 3: Compute per-player stroke holes using their own tee's rankings
  return withAdjusted.map((p) => ({
    ...p,
    strokeHoles: getStrokeHoles(p.adjustedStrokes, p.rankings),
    rankings: undefined, // clean up temp field
  }))
}

/**
 * Build a full (100%) course handicap breakdown for side bets and individual net displays.
 * Unlike buildGameHandicapBreakdown(), this applies NO game-type percentage and NO off-low —
 * every player gets their full course handicap with strokes allocated by hole ranking.
 *
 * Used for: Low Net side bets, Skins (when full_handicap config is true), individual net totals.
 *
 * Handles mixed tees and gender-aware tee ratings, which is why consumers should use this
 * instead of calling calculateCourseHandicap() + getStrokeHoles() inline.
 *
 * @param {Object} params
 * @param {Array} params.matchPlayers - [{ player_id, handicap_index, gender? }]
 * @param {Array} [params.gamePlayers] - [{ player_id }] filter; if omitted, all matchPlayers are used
 * @param {Object} params.course - course object with par[], handicapIndex[]
 * @param {Object} params.tee - tee object with slope, rating (primary/default tee)
 * @param {Object} [params.mixedTees] - { [playerId]: { slope, rating, handicapIndex? } } per-player tee overrides
 * @returns {Array|null} [{ playerId, courseHandicap, strokeHoles: Map }] or null
 */
export function buildSideBetHandicapBreakdown({ matchPlayers, gamePlayers, course, tee, mixedTees }) {
  if (!course || !tee || !matchPlayers?.length) return null

  const totalPar = course.par.reduce((s, p) => s + p, 0)

  const filtered = gamePlayers
    ? matchPlayers.filter((mp) => gamePlayers.some((gp) => gp.player_id === mp.player_id))
    : matchPlayers

  const result = filtered.map((mp) => {
    const playerTee = mixedTees?.[mp.player_id] || tee
    const { slope, rating } = resolvePlayerTeeRatings(playerTee, mp.gender || 'M')
    const courseHandicap = calculateCourseHandicap(Number(mp.handicap_index), slope, rating, totalPar)
    const rankings = resolvePlayerHandicapIndex(playerTee, mp.gender || 'M', course.handicapIndex)
    return {
      playerId: mp.player_id,
      courseHandicap,
      strokeHoles: getStrokeHoles(courseHandicap, rankings),
    }
  })

  return result.length > 0 ? result : null
}
